import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import useAuthStore from '../stores/authStore';
import useProviderProfileStore from '../stores/providerProfileStore';
import { UserRole, USER_ROLE } from '../types/auth';
import { AppLoader } from './ui';

interface ProtectedRouteProps {
	children: React.ReactNode;
	allowedRoles?: UserRole[];
}

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
	const { isAuthenticated, user, isInitialized } = useAuthStore();
	const { profile, profileError } = useProviderProfileStore();
	const location = useLocation();

	// Wait for auth to be initialized before making any redirect decisions
	if (!isInitialized) {
		return <AppLoader message="Loading" variant="fullscreen" />;
	}

	if (!isAuthenticated || !user) {
		return <Navigate to="/login" replace />;
	}

	// Check if user needs to change password (first-time login)
	// Skip this check if already on the change password page
	const isOnChangePasswordPage = location.pathname === '/providers/change-password';

	if (user.passwordChangeRequired && !isOnChangePasswordPage) {
		return <Navigate to="/providers/change-password" replace />;
	}

	// For Salon Owner users, wait for provider profile to load before rendering.
	// Skip on the change-password page which doesn't need profile data.
	// Allow rendering if there's an error (pages can show their own error states).
	if (
		user.role === USER_ROLE.SALON_OWNER &&
		!isOnChangePasswordPage &&
		!profile &&
		!profileError
	) {
		return <AppLoader message="Loading" variant="fullscreen" />;
	}

	if (allowedRoles && !allowedRoles.includes(user.role)) {
		// Redirect based on role
		if (user.role === USER_ROLE.SALON_OWNER) {
			return <Navigate to="/providers/dashboard" replace />;
		}
		return <Navigate to="/dashboard" replace />;
	}

	return <>{children}</>;
};

export default ProtectedRoute;
